import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

function FadeInSection({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); observer.unobserve(el); }
      },
      { threshold: 0.08, rootMargin: '0px 0px -30px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`transition-all duration-800 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`}>
      {children}
    </div>
  );
}

const photoGridItems = [
  {
    image: 'https://readdy.ai/api/search-image?query=Young%20children%20drawing%20with%20chalk%20on%20outdoor%20pavement%20in%20a%20residential%20neighborhood%2C%2035mm%20documentary%20photography%20with%20visible%20grain%2C%20candid%20natural%20play%20moment%2C%20genuine%20children%20engaged%20in%20creative%20activity%2C%20soft%20natural%20daylight%2C%20warm%20earthy%20tones%2C%20simple%20urban%20background%2C%20authentic%20human%20expression%2C%20editorial%20lifestyle%20shot%2C%20realistic%20skin%20texture%2C%20hopeful%20and%20joyful%20mood&width=600&height=400&seq=services-grid-01&orientation=landscape',
    title: 'Holistic Therapeutic Programming',
    description: 'We integrate clinical consultation with recreational and expressive modalities — including aquatics, team sports, visual and performing arts, and community-based recreation — to support healing, emotional regulation, and personal growth within a structured, nurturing milieu.',
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Diverse%20group%20of%20teenagers%20sitting%20around%20a%20table%20playing%20a%20collaborative%20board%20game%20together%2C%2035mm%20documentary%20photography%20with%20visible%20grain%2C%20candid%20natural%20interaction%2C%20genuine%20smiles%20and%20relaxed%20engagement%2C%20warm%20indoor%20lighting%2C%20soft%20neutral%20tones%2C%20simple%20home%20setting%2C%20authentic%20human%20connection%2C%20editorial%20lifestyle%20photography%2C%20realistic%20skin%20texture&width=600&height=400&seq=services-grid-02&orientation=landscape',
    title: 'Psychoeducational Skill Building',
    description: 'Structured learning opportunities address interpersonal effectiveness, emotional literacy, healthy relationship dynamics, conflict navigation, self-regulation strategies, and adaptive coping mechanisms that promote resilience and safer decision-making.',
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Young%20adults%20walking%20and%20talking%20together%20in%20a%20bright%20modern%20residential%20hallway%2C%2035mm%20documentary%20photography%20with%20visible%20grain%2C%20candid%20natural%20conversation%2C%20genuine%20body%20language%2C%20soft%20natural%20daylight%20from%20windows%2C%20warm%20neutral%20interior%20tones%2C%20simple%20clean%20background%2C%20authentic%20human%20interaction%2C%20editorial%20lifestyle%20shot%2C%20realistic%20skin%20texture&width=600&height=400&seq=services-grid-03&orientation=landscape',
    title: 'Daily Living Competency Development',
    description: 'Strength-based coaching cultivates practical self-efficacy across domains of daily life — from personal care routines and household management to transportation navigation and community resource access — fostering autonomy and long-term stability.',
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Two%20professionals%20sitting%20at%20a%20table%20reviewing%20documents%20and%20care%20plans%20together%2C%2035mm%20documentary%20photography%20with%20visible%20grain%2C%20candid%20collaborative%20planning%20moment%2C%20natural%20body%20language%20and%20engagement%2C%20warm%20indoor%20office%20lighting%2C%20soft%20neutral%20tones%2C%20simple%20clean%20background%2C%20authentic%20professional%20connection%2C%20editorial%20documentary%20style%2C%20realistic%20skin%20texture&width=600&height=400&seq=services-grid-04&orientation=landscape',
    title: 'Coordinated Service Planning',
    description: 'Comprehensive needs assessment, interdisciplinary care coordination, and intentional transition planning ensure continuity across placements, agencies, schools, and natural supports so every young person experiences a seamless, wraparound approach to their care journey.',
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Supportive%20counselor%20having%20a%20calm%20one-on-one%20conversation%20with%20a%20young%20person%20in%20a%20comfortable%20living%20room%20setting%2C%2035mm%20documentary%20photography%20with%20visible%20grain%2C%20candid%20authentic%20therapeutic%20moment%2C%20natural%20empathetic%20body%20language%2C%20warm%20soft%20indoor%20lighting%2C%20neutral%20warm%20tones%2C%20simple%20clean%20interior%2C%20real%20human%20connection%2C%20editorial%20documentary%20style%2C%20realistic%20skin%20texture&width=600&height=400&seq=services-grid-05&orientation=landscape',
    title: 'Therapeutic Counselling & Family Support',
    description: 'Trauma-informed individual and family counselling addresses systemic barriers, attachment disruptions, educational advocacy, and psychosocial stressors delivered through a relational lens that prioritizes safety, trust, and collaborative goal-setting.',
  },
  {
    image: 'https://readdy.ai/api/search-image?query=Realistic%20documentary%20photograph%20of%20young%20adults%20volunteering%20together%20at%20a%20community%20garden%20on%20a%20sunny%20afternoon%2C%20hands%20in%20soil%20planting%20vegetables%2C%20genuine%20smiles%20and%20natural%20candid%20interaction%2C%20diverse%20group%20working%20side%20by%20side%2C%20soft%20natural%20daylight%2C%20warm%20earthy%20tones%2C%20simple%20residential%20neighborhood%20background%20with%20wooden%20fence%20and%20greenery%2C%20authentic%20community%20engagement%20moment%2C%2035mm%20film%20aesthetic%20with%20subtle%20grain%2C%20editorial%20reportage%20style%2C%20real%20people%20with%20natural%20expressions%20and%20realistic%20skin%20texture%2C%20hopeful%20and%20grounded%20atmosphere&width=600&height=400&seq=services-grid-06&orientation=landscape&nocache=true',
    title: 'Community Connection & Social Belonging',
    description: 'Facilitated community participation including cultural events, seasonal outings, volunteer placements, and recreational pursuits strengthens prosocial networks, builds collective identity, and nurtures a genuine sense of belonging beyond the residential setting.',
  },
];

export default function Services() {
  const navigate = useNavigate();

  const handleRequest = () => {
    navigate('/contact');
  };

  return (
    <main>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-28 pb-12 md:pt-36 md:pb-16 overflow-hidden bg-background-50">
        <div className="absolute inset-0 top-0 h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20modern%20residential%20home%20interior%20with%20natural%20sunlight%20streaming%20through%20large%20windows%2C%20comfortable%20living%20space%20with%20soft%20neutral%20tones%2C%2035mm%20documentary%20photography%20with%20visible%20grain%2C%20candid%20authentic%20atmosphere%2C%20real%20people%20with%20natural%20skin%20texture%2C%20soft%20warm%20shadows%2C%20inviting%20and%20peaceful%20environment%2C%20clean%20minimalist%20aesthetic%2C%20natural%20materials%20and%20textures%2C%20editorial%20lifestyle%20photography%2C%20hopeful%20calm%20mood&width=1600&height=800&seq=services-hero-2026-v2&orientation=landscape&nocache=true"
            alt="MCT Residential Services - Warm, welcoming residential home interior"
            title="MCT Residential Services - Our Services"
            className="w-full h-full object-cover object-center"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 pt-8 md:pt-12">
          <div className="max-w-3xl">
            <span className="inline-block bg-white/15 backdrop-blur-sm text-white text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider label-font mb-5 border border-white/20">
              Our Services
            </span>
            <h1 className="heading-font text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.08] mb-5 tracking-tight">
              Comprehensive support.<br />Meaningful <span className="italic text-accent-300">outcomes.</span>
            </h1>
            <p className="text-white/75 text-base md:text-lg lg:text-xl leading-relaxed max-w-2xl font-light">
              Every service we offer is grounded in our relationship-based, person-centred philosophy. We design individualized support that honours each person&apos;s strengths, goals, and potential.
            </p>
          </div>
        </div>
      </section>

      {/* Photo Grid — Core Services */}
      <section className="py-12 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <FadeInSection>
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block bg-primary-100/70 text-primary-700 text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider label-font mb-4">
                Residential Programming
              </span>
              <h2 className="heading-font text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight">
                A Continuum of In-Home Support Services
              </h2>
            </div>
          </FadeInSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {photoGridItems.map((item, idx) => (
              <FadeInSection key={item.title} className={idx > 2 ? 'hidden sm:block' : ''}>
                <div className="group bg-background-100 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300">
                  <div className="aspect-[3/2] overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.title}
                      title={item.title}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 md:p-6">
                    <h3 className="heading-font text-lg md:text-xl font-semibold text-foreground-900 mb-2 leading-snug">{item.title}</h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="bg-primary-500 rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-400/30 to-transparent"></div>
            <div className="relative z-10 max-w-2xl mx-auto">
              <span className="w-16 h-16 flex items-center justify-center bg-white/20 text-white rounded-2xl mx-auto mb-6">
                <i className="ri-chat-heart-line text-2xl"></i>
              </span>
              <h2 className="heading-font text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5">
                Ready to discuss how we can support a young person?
              </h2>
              <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8">
                Our team is available 24/7 to discuss placement needs, answer questions, and explore how our services can make a difference.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleRequest}
                  className="inline-flex items-center justify-center gap-2 bg-white text-primary-700 px-8 py-4 rounded-full text-base font-semibold label-font hover:bg-white/90 transition-all duration-300 cursor-pointer whitespace-nowrap group shadow-lg shadow-foreground-950/15"
                >
                  Request Placement
                  <span className="w-5 h-5 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </button>
                <a
                  href="tel:12892046406"
                  className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 rounded-full text-base font-medium label-font hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-line"></i>
                  </span>
                  Call 1 289 204 6406
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}